'use client';

import clsx from 'clsx';
import type { ReactNode, ForwardedRef } from 'react';
import { forwardRef } from '../../../forward-ref.js';
import { withStyleProps } from '../../../with-style-props.js';
import type { Direction, CommonProps } from '../../types.js';
import { getResponsiveVariables, type Responsive, type Spacing } from '../types.js';

export type Alignment = 'start' | 'center' | 'end' | 'stretch';
export type Justify = 'start' | 'center' | 'end' | 'space-around' | 'space-between' | 'space-evenly';

export interface StackProps extends CommonProps {
  /** The items to stack. */
  children: ReactNode;
  /**
   * Determines the stacking direction (ie. which direction is the primary axis)
   * @default 'vertical'
   */
  direction?: Responsive<Direction>;
  /** The amount of space between items. */
  gap: Responsive<Spacing>;
  /**
   * Determines how items are distributed along the primary axis.
   * @default 'start'
   */
  justify?: Responsive<Justify>;
  /**
   * Determines how each item is aligned along the cross axis.
   * @default 'stretch'
   */
  align?: Responsive<Alignment>;
  /**
   * Determines whether items will wrap to a new line if stack size is too small.
   * @default false
   */
  wrap?: Responsive<boolean>;
}

function Stack(
  {
    children,
    direction: responsiveDirection,
    gap: responsiveGap,
    justify: responsiveJustify,
    align: responsiveAlign,
    wrap: responsiveWrap,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: StackProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const gap = getResponsiveVariables('stack-gap', 'space', responsiveGap, 0);
  const direction = getResponsiveVariables('stack-fd', 'stack-direction', responsiveDirection, 'vertical');
  const justify = getResponsiveVariables('stack-jc', 'stack-justify', responsiveJustify, 'start');
  const align = getResponsiveVariables('stack-ai', 'stack-align', responsiveAlign, 'stretch');
  const wrap = getResponsiveVariables('stack-fw', 'stack-wrap', responsiveWrap, false);

  return (
    <div
      {...props}
      ref={ref}
      className={clsx('cim-stack', UNSAFE_className)}
      style={{
        ...UNSAFE_style,
        ...gap,
        ...direction,
        ...justify,
        ...align,
        ...wrap,
      }}
      role="presentation"
    >
      {children}
    </div>
  );
}

/** Stacks its children with a specified amount of space between them. */
const _Stack = withStyleProps(forwardRef(Stack), 'Stack');

export { _Stack as Stack };
